import React, { useState, useRef, useEffect } from "react";

interface EditableProps {
  text: string;
  onEdit: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

const EditableDiv: React.FC<EditableProps> = ({
  text,
  onEdit,
  className = "",
  style = {},
  placeholder = "Click to edit",
}) => {
  const [value, setValue] = useState(text);
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(text);
  }, [text]);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const newValue = e.currentTarget.innerText;
    setValue(newValue);
    onEdit(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      divRef.current?.blur();
    }
  };

  return (
    <div
      ref={divRef}
      contentEditable
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`cursor-text outline-none ${className}`}
      style={{
        ...style,
        transition: "all 0.2s ease",
      }}
      suppressContentEditableWarning
    >
      {value || placeholder}
    </div>
  );
};

interface SlideContent {
  title?: string;
  subtitle?: string;
  topic?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const HeaderTitleSlide4 = {
  id: "header-title4-slide",
  name: "Header Title Slide (Geometric)",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
    },
    {
      id: "topic",
      label: "Topic",
      type: "text",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
    },
  ],
  render: (content: SlideContent) => {
    const {
      title = "",
      subtitle = "",
      topic = "",
      bgColor,
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      // Update the configuration in PreviewSlides
      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Geometric Pattern Background */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute transform rotate-45"
              style={{
                width: Math.random() * 200 + 100 + "px",
                height: Math.random() * 200 + 100 + "px",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                left: Math.random() * 100 + "%",
                top: Math.random() * 100 + "%",
                animation: `float-${i} ${Math.random() * 10 + 20}s linear infinite`,
              }}
            />
          ))}
        </div>

        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/40" />

        {/* Content Container */}
        <div className="relative z-10 flex flex-col h-full">
          {/* Top Bar */}
          <div className="p-8 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="w-8 h-8 border border-white/20 rounded-lg"
                  style={{
                    transform: `rotate(${45 * i}deg)`,
                    animation: `rotate-${i} 4s ${i * 0.5}s infinite linear`,
                  }}
                />
              ))}
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
              <EditableDiv
                text={topic}
                onEdit={handleEdit("topic")}
                className="text-sm font-medium"
                placeholder="Enter topic..."
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-grow flex items-center justify-center p-12">
            <div className="max-w-5xl w-full space-y-12">
              {/* Title */}
              <div className="relative">
                <div className="absolute -inset-4 border border-white/10 rounded-xl -rotate-1"></div>
                <div className="absolute -inset-4 border border-white/10 rounded-xl rotate-1"></div>
                <EditableDiv
                  text={title}
                  onEdit={handleEdit("title")}
                  className="relative text-7xl font-black tracking-tight leading-none text-center animate-scale-in"
                  placeholder="Enter title..."
                />
              </div>

              {/* Animated Lines */}
              <div className="flex justify-center space-x-8">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="w-16 h-px bg-white/30"
                    style={{
                      animation: `width 1s ${i * 0.2}s ease-out forwards`,
                      opacity: 0,
                    }}
                  />
                ))}
              </div>

              {/* Subtitle */}
              <EditableDiv
                text={subtitle}
                onEdit={handleEdit("subtitle")}
                className="text-3xl font-light text-white/90 text-center max-w-3xl mx-auto animate-fade-in"
                placeholder="Enter subtitle..."
              />
            </div>
          </div>

          {/* Bottom Decoration */}
          <div className="p-8 flex justify-center">
            <div className="grid grid-cols-3 gap-2">
              {[...Array(9)].map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-2 border border-white/20 rounded-sm"
                  style={{
                    animation: `pulse 2s ${i * 0.1}s infinite`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  },
};

// Add required styles to the global CSS
const style = document.createElement("style");
style.textContent = `
  @keyframes scale-in {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes width {
    from {
      opacity: 0;
      width: 0;
    }
    to {
      opacity: 1;
      width: 4rem;
    }
  }

  ${[...Array(20)]
    .map(
      (_, i) => `
    @keyframes float-${i} {
      0% {
        transform: translate(0, 0) rotate(45deg);
      }
      50% {
        transform: translate(${Math.random() * 100 - 50}px, ${Math.random() * 100 - 50}px) rotate(${45 + Math.random() * 90}deg);
      }
      100% {
        transform: translate(0, 0) rotate(45deg);
      }
    }
  `,
    )
    .join("\n")}

  ${[...Array(3)]
    .map(
      (_, i) => `
    @keyframes rotate-${i} {
      from { transform: rotate(${45 * i}deg); }
      to { transform: rotate(${45 * i + 360}deg); }
    }
  `,
    )
    .join("\n")}

  .animate-scale-in {
    animation: scale-in 0.8s ease-out forwards;
  }

  .animate-fade-in {
    animation: fade-in 0.8s ease-out forwards;
  }
`;
document.head.appendChild(style);

// Declare global window interface
declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default HeaderTitleSlide4;
